/**
 * AutomationDetailPage — full editor for a single automation.
 *
 * Layout:
 *   - Header:      name (inline editable), status toggle, save/back
 *   - Left column: workflow visualiser — trigger + step list, click any
 *                  step to select it for editing
 *   - Right column: contextual editor for the selected step
 *   - Below:       recent runs
 *
 * Every step type has its own tiny editor form; email + SMS steps have
 * rich body/subject editors with a merge-field picker.
 */
import { useEffect, useMemo, useRef, useState } from "react"
import { Link, useNavigate, useParams } from "react-router-dom"
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query"
import { useAuth } from "@/hooks/useAuth"
import {
  ArrowLeft,
  ChevronRight,
  Loader2,
  Mail,
  MessageSquare,
  Clock,
  CheckSquare,
  StickyNote,
  Tag,
  TagIcon,
  GitBranch,
  Route,
  User,
  Copy,
  Sparkles,
  AlertCircle,
  Save,
  Send,
  Check,
  Paperclip,
  Upload,
  X,
  FileText,
  PenLine,
  Zap,
  Users,
} from "lucide-react"
import { AppShell } from "@/components/AppShell"
import { Card } from "@/components/ui/card"
import { Button } from "@/components/ui/button"

import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { Textarea } from "@/components/ui/textarea"
import { RichTextEditor } from "@/components/RichTextEditor"
import { BulkEnrolContactsDialog } from "@/components/BulkEnrolContactsDialog"
import {
  api,
  automationsApi,
  OPERATIONAL_REASON_LABELS,
  type AutomationCategory,
  type AutomationWorkflow,
  type AutomationCommunicationType,
  type AutomationRun,
  type OperationalReason,
  type StepClassification,
} from "@/lib/api"
import { formatTrigger, formatStep, CATEGORY_META } from "@/pages/automations/templates"
import { cn } from "@/lib/utils"
import { timeAgo, timeUntil, formatDateTime, formatDate } from "@shared/utils/date"

type Step = AutomationWorkflow["steps"][number]

// ============================================================
// Merge fields available in email/SMS bodies
// ============================================================
const MERGE_FIELDS: { key: string; label: string; group: string }[] = [
  { key: "contact.firstName", label: "First name", group: "Contact" },
  { key: "contact.lastName", label: "Last name", group: "Contact" },
  { key: "contact.email", label: "Email", group: "Contact" },
  { key: "contact.mobile", label: "Mobile", group: "Contact" },
  { key: "contact.businessName", label: "Business name", group: "Contact" },
  { key: "deal.dealName", label: "Deal name", group: "Deal" },
  { key: "deal.loanAmount", label: "Loan amount", group: "Deal" },
  { key: "deal.balloon", label: "Balloon amount ($)", group: "Deal" },
  { key: "deal.termEndDate", label: "Term end date", group: "Deal" },
  { key: "deal.termEndDateFormatted", label: "Term end date (formatted)", group: "Deal" },
  { key: "deal.termEndIn", label: "Time until term ends (e.g. \"3 months\")", group: "Deal" },
  { key: "deal.settledDate", label: "Settlement date", group: "Deal" },
  { key: "deal.settledDateFormatted", label: "Settlement date (formatted)", group: "Deal" },
  { key: "deal.settledAgo", label: "Time since settlement (e.g. \"12 months ago\")", group: "Deal" },
  { key: "broker.firstName", label: "Broker first name", group: "Broker" },
  { key: "broker.email", label: "Broker email", group: "Broker" },
  { key: "broker.mobile", label: "Broker mobile", group: "Broker" },
  { key: "today", label: "Today's date", group: "Other" },
]

// ============================================================
// Communication classification cards (Phase 3)
// ============================================================

const OPERATIONAL_REASONS: OperationalReason[] = [
  "responding_to_enquiry",
  "active_finance_application",
  "document_request",
  "appointment_reminder",
  "approval_update",
  "settlement_update",
  "client_requested_information",
  "current_service_request",
  "other",
]

function CommunicationTypeCard({
  workflow,
  onChange,
}: {
  workflow: AutomationWorkflow
  onChange: (patch: Partial<AutomationWorkflow>) => void
}) {
  const type: AutomationCommunicationType = workflow.communicationType ?? "marketing"
  const reason = workflow.operationalReason
  const notes = workflow.operationalReasonNotes ?? ""

  return (
    <div className="mt-3 rounded-md border border-input bg-muted/30 p-3">
      <div className="mb-2 flex items-center justify-between">
        <div>
          <div className="text-sm font-medium text-foreground">Communication type</div>
          <div className="text-muted-foreground text-xs">
            Spam Act 2003 / ACMA compliance classification for this automation.
          </div>
        </div>
      </div>
      <div className="grid gap-2 sm:grid-cols-3">
        {(
          [
            {
              value: "direct",
              label: "Direct / Operational",
              hint: "Enquiries, applications, appointments, documents, settlements.",
            },
            {
              value: "marketing",
              label: "Marketing / Promotional",
              hint: "Nurture, newsletters, promotions, refinance, birthdays.",
            },
            {
              value: "mixed",
              label: "Mixed — classify per message",
              hint: "Each SMS/Email step gets its own classification.",
            },
          ] as const
        ).map((opt) => {
          const active = opt.value === type
          return (
            <button
              key={opt.value}
              type="button"
              onClick={() =>
                onChange({
                  communicationType: opt.value,
                  // Clear operational reason when switching away from direct
                  ...(opt.value !== "direct"
                    ? { operationalReason: undefined, operationalReasonNotes: undefined }
                    : {}),
                })
              }
              className={cn(
                "rounded-md border p-3 text-left transition-colors",
                active
                  ? "border-rc-blue-500 bg-rc-blue-50/60"
                  : "border-border bg-background hover:border-rc-blue-300"
              )}
            >
              <div className="text-sm font-semibold text-foreground">{opt.label}</div>
              <div className="text-muted-foreground mt-0.5 text-[11px] leading-tight">{opt.hint}</div>
            </button>
          )
        })}
      </div>

      {type === "direct" && (
        <div className="mt-3 space-y-2">
          <div>
            <Label className="text-xs">Operational reason</Label>
            <select
              className="border-input bg-background mt-1 h-10 w-full rounded-md border px-3 text-sm"
              value={reason ?? ""}
              onChange={(e) =>
                onChange({
                  operationalReason: (e.target.value || undefined) as OperationalReason | undefined,
                })
              }
            >
              <option value="">Select a reason…</option>
              {OPERATIONAL_REASONS.map((r) => (
                <option key={r} value={r}>
                  {OPERATIONAL_REASON_LABELS[r]}
                </option>
              ))}
            </select>
          </div>
          {reason === "other" && (
            <div>
              <Label className="text-xs">Explain the operational reason</Label>
              <Textarea
                value={notes}
                onChange={(e) => onChange({ operationalReasonNotes: e.target.value })}
                rows={2}
                placeholder="Describe why this qualifies as operational, not marketing…"
              />
            </div>
          )}
          <div className="border-amber-200 bg-amber-50 rounded-md border p-2 text-[11px] leading-snug text-amber-900">
            <strong>Direct messages must relate specifically</strong> to an enquiry,
            application, appointment, document, or service the client requested. Do NOT
            use this classification for promotions, newsletters, general lead nurture,
            refinance advertising, or to bypass opt-out requirements. Misuse is logged
            for compliance review.
          </div>
        </div>
      )}
      {type === "marketing" && (
        <p className="text-muted-foreground mt-2 text-[11px]">
          Every SMS/Email step will automatically include opt-out wording that cannot
          be disabled. Contacts unsubscribed from marketing on this channel will be
          skipped before every send.
        </p>
      )}
      {type === "mixed" && (
        <p className="text-muted-foreground mt-2 text-[11px]">
          Each SMS/Email step needs its own classification. Direct steps may omit
          opt-out wording; marketing steps always include it.
        </p>
      )}
    </div>
  )
}

function StepClassificationCard({
  step,
  onChange,
  channel,
}: {
  step: Step
  onChange: (patch: Partial<Step>) => void
  channel: "sms" | "email"
}) {
  const cls = (step as { classification?: StepClassification }).classification
  const reason = (step as { operationalReason?: OperationalReason }).operationalReason
  const notes = (step as { operationalReasonNotes?: string }).operationalReasonNotes ?? ""

  return (
    <div className="mb-3 rounded-md border border-input bg-muted/20 p-3">
      <div className="mb-2 text-sm font-medium text-foreground">
        {channel === "sms" ? "SMS" : "Email"} classification
      </div>
      <div className="grid gap-2 sm:grid-cols-2">
        {(
          [
            { value: "direct", label: "Direct / Operational" },
            { value: "marketing", label: "Marketing / Promotional" },
          ] as const
        ).map((opt) => {
          const active = cls === opt.value
          return (
            <button
              key={opt.value}
              type="button"
              onClick={() =>
                onChange({
                  classification: opt.value,
                  ...(opt.value !== "direct"
                    ? { operationalReason: undefined, operationalReasonNotes: undefined }
                    : {}),
                } as Partial<Step>)
              }
              className={cn(
                "rounded-md border p-2 text-left text-sm transition-colors",
                active
                  ? "border-rc-blue-500 bg-rc-blue-50/60 font-semibold"
                  : "border-border bg-background hover:border-rc-blue-300"
              )}
            >
              {opt.label}
            </button>
          )
        })}
      </div>

      {cls === "direct" && (
        <div className="mt-3 space-y-2">
          <div>
            <Label className="text-xs">Operational reason</Label>
            <select
              className="border-input bg-background mt-1 h-9 w-full rounded-md border px-3 text-sm"
              value={reason ?? ""}
              onChange={(e) =>
                onChange({
                  operationalReason: (e.target.value || undefined) as
                    | OperationalReason
                    | undefined,
                } as Partial<Step>)
              }
            >
              <option value="">Select a reason…</option>
              {OPERATIONAL_REASONS.map((r) => (
                <option key={r} value={r}>
                  {OPERATIONAL_REASON_LABELS[r]}
                </option>
              ))}
            </select>
          </div>
          {reason === "other" && (
            <div>
              <Label className="text-xs">Explain</Label>
              <Textarea
                value={notes}
                onChange={(e) =>
                  onChange({ operationalReasonNotes: e.target.value } as Partial<Step>)
                }
                rows={2}
              />
            </div>
          )}
        </div>
      )}
      {!cls && (
        <p className="text-amber-700 mt-2 text-[11px]">
          ⚠ Pick a classification. Mixed workflows require it on every send step
          before you can save.
        </p>
      )}
    </div>
  )
}

// ============================================================
// Page
// ============================================================
export default function AutomationDetailPage() {
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const qc = useQueryClient()

  const query = useQuery({
    queryKey: ["automation", id],
    queryFn: () => automationsApi.get(id!),
    enabled: !!id,
  })

  // Local edit state — drives everything until we save
  const [draft, setDraft] = useState<{
    name: string
    description: string
    category: AutomationCategory
    smsEnabled: boolean
    emailEnabled: boolean
    workflow: AutomationWorkflow
  } | null>(null)
  const [selectedStepIdx, setSelectedStepIdx] = useState<number>(0)
  const [enrolOpen, setEnrolOpen] = useState(false)

  // Sync draft when API data loads
  useEffect(() => {
    if (query.data) {
      const a = query.data.automation
      setDraft({
        name: a.name,
        description: a.description ?? "",
        category: (a.category ?? "other") as AutomationCategory,
        smsEnabled: a.smsEnabled ?? true,
        emailEnabled: a.emailEnabled ?? true,
        workflow: a.workflow,
      })
      setSelectedStepIdx(0)
    }
  }, [query.data])

  const original = query.data?.automation
  const dirty = useMemo(() => {
    if (!draft || !original) return false
    return (
      draft.name !== original.name ||
      draft.description !== (original.description ?? "") ||
      draft.category !== ((original.category ?? "other") as AutomationCategory) ||
      draft.smsEnabled !== (original.smsEnabled ?? true) ||
      draft.emailEnabled !== (original.emailEnabled ?? true) ||
      JSON.stringify(draft.workflow) !== JSON.stringify(original.workflow)
    )
  }, [draft, original])

  const save = useMutation({
    mutationFn: async () => {
      if (!draft || !id) throw new Error("nothing to save")
      return automationsApi.update(id, {
        name: draft.name,
        description: draft.description || null,
        category: draft.category,
        smsEnabled: draft.smsEnabled,
        emailEnabled: draft.emailEnabled,
        workflow: draft.workflow,
      })
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["automation", id] })
      qc.invalidateQueries({ queryKey: ["automations"] })
    },
  })

  const activate = useMutation({
    mutationFn: () => automationsApi.activate(id!),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["automation", id] })
      qc.invalidateQueries({ queryKey: ["automations"] })
    },
  })
  const pause = useMutation({
    mutationFn: () => automationsApi.pause(id!, "paused"),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["automation", id] })
      qc.invalidateQueries({ queryKey: ["automations"] })
    },
  })

  if (query.isLoading || !draft || !original) {
    return (
      <AppShell title="Automation">
        <Card className="grid place-items-center p-16">
          <Loader2 className="text-muted-foreground h-6 w-6 animate-spin" />
        </Card>
      </AppShell>
    )
  }
  if (query.error) {
    return (
      <AppShell title="Automation">
        <Card className="p-6 text-sm text-destructive">
          {query.error instanceof Error ? query.error.message : "Failed to load"}
        </Card>
      </AppShell>
    )
  }

  const status = original.status
  const isActive = status === "active"
  const selectedStep = draft.workflow.steps[selectedStepIdx] ?? null

  const updateStep = (idx: number, patch: Partial<Step>) => {
    setDraft((d) => {
      if (!d) return d
      const nextSteps = [...d.workflow.steps]
      nextSteps[idx] = { ...nextSteps[idx], ...patch } as Step
      return { ...d, workflow: { ...d.workflow, steps: nextSteps } }
    })
  }

  const actions = (
    <div className="flex flex-wrap items-center gap-2">
      {dirty && (
        <span className="text-amber-700 text-xs font-medium">Unsaved changes</span>
      )}
      {isActive && (
        <>
          <Button
            variant="outline"
            onClick={() => setEnrolOpen(true)}
            disabled={dirty}
            title={dirty ? "Save your changes first" : "Enrol contacts into this automation"}
          >
            <Zap className="h-4 w-4" />
            Enrol contacts
          </Button>
          <Button
            variant="outline"
            onClick={() => {
              const el = document.getElementById("enrolled-contacts")
              if (el) el.scrollIntoView({ behavior: "smooth", block: "start" })
            }}
            title="View + remove currently enrolled contacts"
          >
            <Users className="h-4 w-4" />
            Enrolled
          </Button>
        </>
      )}
      {status !== "archived" && (
        <Button
          variant="outline"
          onClick={() => (isActive ? pause.mutate() : activate.mutate())}
          disabled={activate.isPending || pause.isPending}
        >
          {isActive ? "Pause" : "Activate"}
        </Button>
      )}
      <Button onClick={() => save.mutate()} disabled={!dirty || save.isPending}>
        {save.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
        Save changes
      </Button>
    </div>
  )

  return (
    <AppShell
      title={draft.name || "Automation"}
      subtitle={formatTrigger(
        draft.workflow.trigger.kind,
        draft.workflow.trigger.params ?? {}
      )}
      actions={actions}
    >
      <div className="mb-3">
        <Button
          variant="outline"
          size="sm"
          onClick={() => navigate("/automations")}
          className="gap-1"
        >
          <ArrowLeft className="h-3.5 w-3.5" />
          Back to automations
        </Button>
      </div>

      {save.error && (
        <div className="border-destructive/40 bg-destructive/5 text-destructive mb-3 flex items-start gap-2 rounded-md border p-2.5 text-sm">
          <AlertCircle className="mt-0.5 h-4 w-4 shrink-0" />
          <span>{save.error instanceof Error ? save.error.message : "Save failed"}</span>
        </div>
      )}

      {/* Meta block */}
      <Card className="mb-4 p-4">
        <div className="grid grid-cols-1 gap-3 md:grid-cols-3">
          <div>
            <Label>Name</Label>
            <Input
              value={draft.name}
              onChange={(e) => setDraft({ ...draft, name: e.target.value })}
              placeholder="Automation name"
            />
          </div>
          <div>
            <Label>Description</Label>
            <Input
              value={draft.description}
              onChange={(e) => setDraft({ ...draft, description: e.target.value })}
              placeholder="Short description shown on cards"
            />
          </div>
          <div>
            <Label>Category</Label>
            <select
              className="border-input bg-background text-foreground h-10 w-full rounded-md border px-3 text-sm focus:outline-none focus:ring-2 focus:ring-ring/20"
              value={draft.category}
              onChange={(e) =>
                setDraft({ ...draft, category: e.target.value as AutomationCategory })
              }
            >
              {(Object.keys(CATEGORY_META) as AutomationCategory[]).map((c) => (
                <option key={c} value={c}>
                  {CATEGORY_META[c].label}
                </option>
              ))}
            </select>
          </div>
        </div>

        {/* Channel kill-switches. When off, matching send steps are logged
            as skipped in the communications timeline instead of firing. */}
        <div className="mt-3 flex flex-col gap-2 rounded-md border border-input bg-muted/30 p-3 sm:flex-row sm:items-center sm:justify-between">
          <div>
            <div className="text-sm font-medium text-foreground">Send channels</div>
            <div className="text-muted-foreground text-xs">
              Turn off to stop this automation sending SMS or email (steps run and log as "skipped").
              Applies to any templates used inside the automation.
            </div>
          </div>
          <div className="flex gap-4">
            <label className="flex cursor-pointer items-center gap-2 text-sm">
              <input
                type="checkbox"
                className="h-4 w-4 rounded border-input"
                checked={draft.smsEnabled}
                onChange={(e) => setDraft({ ...draft, smsEnabled: e.target.checked })}
              />
              <span>Send SMS</span>
            </label>
            <label className="flex cursor-pointer items-center gap-2 text-sm">
              <input
                type="checkbox"
                className="h-4 w-4 rounded border-input"
                checked={draft.emailEnabled}
                onChange={(e) => setDraft({ ...draft, emailEnabled: e.target.checked })}
              />
              <span>Send Email</span>
            </label>
          </div>
        </div>
        {/* Communication Type (Phase 3 — Spam Act / ACMA classification) */}
        <CommunicationTypeCard
          workflow={draft.workflow}
          onChange={(patch) =>
            setDraft({
              ...draft,
              workflow: { ...draft.workflow, ...patch },
            })
          }
        />

        <div className="text-muted-foreground mt-3 flex flex-wrap gap-x-4 gap-y-1 text-xs">
          <EnrolmentCountBadge automationId={id!} />
          <span>Created {formatDateTime(original.createdAt)}</span>
          <span>Updated {timeAgo(original.updatedAt)}</span>
          <span>
            Status:{" "}
            <StatusBadge status={status} />
          </span>
        </div>
      </Card>

      {/* Editor grid */}
      <div className="grid grid-cols-1 gap-4 xl:grid-cols-[minmax(0,1fr)_minmax(0,1.4fr)]">
        {/* Left: workflow map */}
        <Card className="p-4">
          <div className="mb-2 flex items-center justify-between">
            <h3 className="text-sm font-semibold text-foreground">Workflow</h3>
            <span className="text-muted-foreground text-xs">
              {draft.workflow.steps.length} step{draft.workflow.steps.length === 1 ? "" : "s"}
            </span>
          </div>

          {/* Trigger tile */}
          <div className="border-rc-blue-300 bg-rc-blue-50 mb-2 rounded-md border-2 px-3 py-2">
            <div className="text-rc-blue-700 text-[10px] font-bold uppercase tracking-wide">
              Trigger
            </div>
            <div className="text-foreground mt-0.5 text-sm font-medium">
              {formatTrigger(
                draft.workflow.trigger.kind,
                draft.workflow.trigger.params ?? {}
              )}
            </div>
          </div>

          {/* Steps */}
          <ol className="space-y-1.5">
            {draft.workflow.steps.map((s, i) => {
              const active = i === selectedStepIdx
              return (
                <li key={s.id}>
                  <button
                    type="button"
                    onClick={() => setSelectedStepIdx(i)}
                    className={cn(
                      "w-full rounded-md border px-3 py-2 text-left text-sm transition-colors",
                      active
                        ? "border-rc-blue-500 bg-rc-blue-50/60"
                        : "border-border bg-background hover:border-rc-blue-300 hover:bg-rc-grey-50"
                    )}
                  >
                    <div className="flex items-center gap-2">
                      <StepIcon kind={s.kind} />
                      <span className="min-w-0 flex-1 truncate text-foreground">
                        {formatStep(s)}
                      </span>
                      {active && (
                        <ChevronRight className="text-rc-blue-600 h-4 w-4 shrink-0" />
                      )}
                    </div>
                  </button>
                </li>
              )
            })}
          </ol>
        </Card>

        {/* Right: step editor */}
        <Card className="p-4">
          {!selectedStep ? (
            <div className="text-muted-foreground grid place-items-center p-8 text-sm">
              Pick a step to edit
            </div>
          ) : (
            <StepEditor
              step={selectedStep}
              onChange={(patch) => updateStep(selectedStepIdx, patch)}
              triggerKind={draft.workflow.trigger.kind}
              workflowCommunicationType={draft.workflow.communicationType ?? "marketing"}
            />
          )}
        </Card>
      </div>

      {/* Enrolled contacts — sits below the builder so brokers see it after
          editing the workflow. Also linked from a header button so scrolling
          isn't required. */}
      <div id="enrolled-contacts">
        <EnrolmentsPanel
          automationId={id!}
          triggerKind={draft.workflow.trigger.kind}
        />
      </div>

      {/* Bulk enrol dialog */}
      <BulkEnrolContactsDialog
        open={enrolOpen}
        onOpenChange={setEnrolOpen}
        automationId={id!}
        automationName={draft.name || "this automation"}
      />
    </AppShell>
  )
}

// ============================================================
// StatusPill — small badge for run status.
//
// When `sendStats` is present and includes at least one opt-out skip, we
// render an extra "🚫 Opted out" pill next to the status so brokers immediately
// see why a run went quiet even if the run itself technically completed.
// ============================================================
function StatusPill({
  status,
  sendStats,
}: {
  status: string
  sendStats?: {
    sent: number
    skipped: number
    failed: number
    optOutSkip: boolean
  }
}) {
  const map: Record<string, { label: string; cls: string }> = {
    queued: { label: "Queued", cls: "bg-slate-100 text-slate-700" },
    running: { label: "Running", cls: "bg-blue-100 text-blue-700" },
    waiting: { label: "Waiting", cls: "bg-amber-100 text-amber-800" },
    completed: { label: "Completed", cls: "bg-emerald-100 text-emerald-700" },
    exited: { label: "Exited", cls: "bg-emerald-100 text-emerald-700" },
    cancelled: { label: "Removed", cls: "bg-rc-grey-100 text-rc-grey-700" },
    failed: { label: "Failed", cls: "bg-red-100 text-red-700" },
  }
  const s = map[status] ?? { label: status, cls: "bg-rc-grey-100 text-rc-grey-700" }
  return (
    <span className="inline-flex items-center gap-1">
      <span
        className={cn(
          "inline-block rounded px-1.5 py-0.5 text-[10px] font-medium",
          s.cls
        )}
      >
        {s.label}
      </span>
      {sendStats?.optOutSkip && (
        <span
          className="inline-flex items-center gap-0.5 rounded bg-rose-100 px-1.5 py-0.5 text-[10px] font-medium text-rose-700"
          title={`${sendStats.skipped} send${
            sendStats.skipped === 1 ? "" : "s"
          } skipped — contact opted out (STOP or DNC).`}
        >
          🚫 Opted out
        </span>
      )}
      {!sendStats?.optOutSkip &&
        sendStats &&
        sendStats.skipped > 0 && (
          <span
            className="inline-block rounded bg-amber-100 px-1.5 py-0.5 text-[10px] font-medium text-amber-800"
            title={`${sendStats.skipped} skipped, ${sendStats.sent} sent, ${sendStats.failed} failed`}
          >
            {sendStats.skipped} skipped
          </span>
        )}
    </span>
  )
}

// ============================================================
// EnrolmentCountBadge — tiny live "N enrolled" in the header,
// clickable to jump to the enrolled-contacts panel.
// ============================================================
function EnrolmentCountBadge({ automationId }: { automationId: string }) {
  const runsQuery = useQuery({
    queryKey: ["automation-runs", automationId],
    queryFn: () => automationsApi.runs(automationId),
    refetchInterval: 15_000,
  })
  const activeCount =
    runsQuery.data?.items.filter((r) =>
      ["queued", "running", "waiting"].includes(r.status)
    ).length ?? 0
  const jumpToPanel = () => {
    const el = document.getElementById("enrolled-contacts")
    if (el) el.scrollIntoView({ behavior: "smooth", block: "start" })
  }
  if (activeCount === 0) {
    return <span className="text-muted-foreground">Nobody enrolled yet</span>
  }
  return (
    <button
      type="button"
      onClick={jumpToPanel}
      className="inline-flex items-center gap-1 text-rc-blue-600 hover:text-rc-blue-700 hover:underline"
      title="Scroll to enrolled contacts"
    >
      <span className="font-medium">{activeCount}</span>{" "}
      {activeCount === 1 ? "contact enrolled" : "contacts enrolled"}
      <span className="text-[11px] opacity-70">(manage ↓)</span>
    </button>
  )
}

// ============================================================
// EnrolmentsPanel — who's currently in this automation, with Remove
// ============================================================
type EnrolmentFilter = "active" | "completed" | "cancelled" | "failed" | "all"

function EnrolmentsPanel({
  automationId,
  triggerKind,
}: {
  automationId: string
  triggerKind?: string
}) {
  const qc = useQueryClient()
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set())
  const [confirmRemoveAll, setConfirmRemoveAll] = useState(false)
  const [filter, setFilter] = useState<EnrolmentFilter>("all")

  const runsQuery = useQuery({
    queryKey: ["automation-runs", automationId],
    queryFn: () => automationsApi.runs(automationId),
    refetchInterval: 15_000,
  })

  const removeOne = useMutation({
    mutationFn: (runId: string) => automationsApi.cancelRun(automationId, runId),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["automation-runs", automationId] })
      qc.invalidateQueries({ queryKey: ["automation", automationId] })
    },
  })

  const bulkRemove = useMutation({
    mutationFn: (opts: { runIds?: string[]; all?: boolean }) =>
      automationsApi.bulkCancelRuns(automationId, opts),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["automation-runs", automationId] })
      qc.invalidateQueries({ queryKey: ["automation", automationId] })
      setSelectedIds(new Set())
      setConfirmRemoveAll(false)
    },
  })

  const items = runsQuery.data?.items ?? []

  // Map trigger kind → column label + which deal-date field to render. When
  // the trigger isn't tied to a date field (event triggers), we omit the
  // column entirely so brokers see one less "—" column.
  const triggerRefDateLabel: string | null = (() => {
    switch (triggerKind) {
      case "term_end":
      case "balloon_due":
        return "Term ends"
      case "settlement_anniversary":
        return "Settled"
      case "days_since_deal_lost":
        return "Lost"
      default:
        return null
    }
  })()
  const triggerRefDateFor = (r: AutomationRun): string | null => {
    switch (triggerKind) {
      case "term_end":
      case "balloon_due":
        return r.dealTermEndDate ?? null
      case "settlement_anniversary":
        return r.dealSettledDate ?? null
      case "days_since_deal_lost":
        return r.dealLostDate ?? null
      default:
        return null
    }
  }

  const activeItems = items.filter((r) =>
    ["queued", "running", "waiting"].includes(r.status)
  )
  const completedItems = items.filter(
    (r) => r.status === "completed" || r.status === "exited"
  )
  const cancelledItems = items.filter((r) => r.status === "cancelled")
  const failedItems = items.filter((r) => r.status === "failed")

  const visibleItems =
    filter === "active"
      ? activeItems
      : filter === "completed"
        ? completedItems
        : filter === "cancelled"
          ? cancelledItems
          : filter === "failed"
            ? failedItems
            : items

  const toggleOne = (id: string) => {
    setSelectedIds((cur) => {
      const next = new Set(cur)
      if (next.has(id)) next.delete(id)
      else next.add(id)
      return next
    })
  }

  // Only ACTIVE rows are selectable/removable — you can't "remove" completed history.
  const selectableInView = visibleItems.filter((r) =>
    ["queued", "running", "waiting"].includes(r.status)
  )
  const allSelected =
    selectableInView.length > 0 &&
    selectableInView.every((r) => selectedIds.has(r.id))

  const toggleSelectAll = () => {
    setSelectedIds((cur) => {
      const next = new Set(cur)
      if (allSelected) {
        for (const r of selectableInView) next.delete(r.id)
      } else {
        for (const r of selectableInView) next.add(r.id)
      }
      return next
    })
  }

  const contactLabel = (r: {
    contactFirstName?: string | null
    contactLastName?: string | null
    contactBusinessName?: string | null
    contactId: string | null
  }) => {
    const name = [r.contactFirstName, r.contactLastName].filter(Boolean).join(" ").trim()
    if (name) return name
    if (r.contactBusinessName) return r.contactBusinessName
    if (r.contactId) return r.contactId.slice(0, 8) + "…"
    return "—"
  }

  return (
    <Card className="mt-4 p-4">
      <div className="flex items-center justify-between mb-3 gap-4 flex-wrap">
        <div>
          <h3 className="text-sm font-semibold text-foreground">
            Enrolled contacts
          </h3>
          <p className="text-xs text-muted-foreground mt-0.5">
            <span className="font-medium text-foreground">{activeItems.length}</span> currently
            active · {completedItems.length} completed · {cancelledItems.length} removed
            {failedItems.length > 0 && ` · ${failedItems.length} failed`}
          </p>
        </div>
        <div className="flex items-center gap-2">
          {selectedIds.size > 0 && (
            <Button
              size="sm"
              variant="outline"
              onClick={() => bulkRemove.mutate({ runIds: [...selectedIds] })}
              disabled={bulkRemove.isPending}
              className="text-red-700 border-red-200 hover:bg-red-50"
            >
              {bulkRemove.isPending ? (
                <Loader2 className="h-3.5 w-3.5 animate-spin" />
              ) : (
                <X className="h-3.5 w-3.5" />
              )}
              Remove {selectedIds.size}
            </Button>
          )}
          {activeItems.length > 0 && selectedIds.size === 0 && (
            <Button
              size="sm"
              variant="outline"
              onClick={() => setConfirmRemoveAll(true)}
              disabled={bulkRemove.isPending}
              className="text-red-700 border-red-200 hover:bg-red-50"
            >
              Remove all active
            </Button>
          )}
        </div>
      </div>

      {/* Filter tabs — GHL-style */}
      <div className="flex items-center gap-1 mb-3 border-b border-border">
        {([
          ["all", `All (${items.length})`],
          ["active", `Active (${activeItems.length})`],
          ["completed", `Completed (${completedItems.length})`],
          ["cancelled", `Removed (${cancelledItems.length})`],
          ...(failedItems.length > 0
            ? ([["failed", `Failed (${failedItems.length})`]] as const)
            : []),
        ] as const).map(([key, label]) => (
          <button
            key={key}
            type="button"
            onClick={() => {
              setFilter(key as EnrolmentFilter)
              setSelectedIds(new Set())
            }}
            className={cn(
              "px-3 py-1.5 text-xs font-medium border-b-2 -mb-px transition-colors",
              filter === key
                ? "border-b-rc-blue-600 text-rc-blue-700"
                : "border-b-transparent text-muted-foreground hover:text-foreground"
            )}
          >
            {label}
          </button>
        ))}
      </div>

      {confirmRemoveAll && (
        <div className="mb-3 rounded-md border border-red-200 bg-red-50 p-3">
          <div className="text-sm text-red-900 mb-2">
            Remove all {activeItems.length} contacts from this automation? Any pending steps
            won't run. Already-sent messages can't be unsent.
          </div>
          <div className="flex gap-2">
            <Button
              size="sm"
              onClick={() => bulkRemove.mutate({ all: true })}
              disabled={bulkRemove.isPending}
              className="bg-red-600 hover:bg-red-700"
            >
              {bulkRemove.isPending ? (
                <>
                  <Loader2 className="h-3.5 w-3.5 animate-spin mr-1" />
                  Removing…
                </>
              ) : (
                `Yes, remove ${activeItems.length}`
              )}
            </Button>
            <Button
              size="sm"
              variant="outline"
              onClick={() => setConfirmRemoveAll(false)}
              disabled={bulkRemove.isPending}
            >
              Nevermind
            </Button>
          </div>
        </div>
      )}

      {runsQuery.isLoading ? (
        <div className="flex items-center gap-2 p-4 text-sm text-muted-foreground">
          <Loader2 className="h-4 w-4 animate-spin" />
          Loading…
        </div>
      ) : visibleItems.length === 0 ? (
        <div className="p-6 text-center text-sm text-muted-foreground">
          {filter === "active"
            ? 'No contacts currently active. Use "Enrol contacts" at the top-right to add some.'
            : filter === "all" && items.length === 0
              ? 'Nobody\'s ever been enrolled. Use "Enrol contacts" at the top-right to start.'
              : `No ${filter} enrolments.`}
        </div>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-border text-xs uppercase tracking-wide text-muted-foreground">
                <th className="w-8 py-2 pl-2">
                  {selectableInView.length > 0 && (
                    <input
                      type="checkbox"
                      className="h-4 w-4 rounded border-input"
                      checked={allSelected}
                      onChange={toggleSelectAll}
                      title="Select all active in view"
                    />
                  )}
                </th>
                <th className="py-2 text-left font-medium">Contact</th>
                <th className="py-2 text-left font-medium">Deal</th>
                {triggerRefDateLabel && (
                  <th className="py-2 text-left font-medium">{triggerRefDateLabel}</th>
                )}
                <th className="py-2 text-left font-medium">Status</th>
                <th className="py-2 text-left font-medium">Step</th>
                <th className="py-2 text-left font-medium">Enrolled</th>
                <th className="py-2"></th>
              </tr>
            </thead>
            <tbody>
              {visibleItems.slice(0, 200).map((r) => {
                const isActive = ["queued", "running", "waiting"].includes(r.status)
                const isSelected = selectedIds.has(r.id)
                return (
                  <tr
                    key={r.id}
                    className={`border-b border-border ${
                      isSelected
                        ? "bg-blue-50/40"
                        : isActive
                          ? "hover:bg-accent/40"
                          : "opacity-70 hover:opacity-100 hover:bg-accent/20"
                    }`}
                  >
                    <td className="py-2 pl-2">
                      {isActive && (
                        <input
                          type="checkbox"
                          className="h-4 w-4 rounded border-input"
                          checked={isSelected}
                          onChange={() => toggleOne(r.id)}
                        />
                      )}
                    </td>
                    <td className="py-2">
                      {r.contactId ? (
                        <Link
                          to={`/contacts/${r.contactId}`}
                          className="text-rc-blue-600 hover:text-rc-blue-700 truncate max-w-[220px] inline-block font-medium"
                        >
                          {contactLabel(r)}
                        </Link>
                      ) : (
                        <span className="text-muted-foreground">—</span>
                      )}
                    </td>
                    <td className="py-2">
                      {r.dealId ? (
                        <Link
                          to={`/deals/${r.dealId}`}
                          className="text-rc-blue-600 hover:text-rc-blue-700 truncate max-w-[180px] inline-block"
                        >
                          {r.dealName ?? "Deal"}
                        </Link>
                      ) : (
                        <span className="text-muted-foreground">—</span>
                      )}
                    </td>
                    {triggerRefDateLabel && (
                      <td className="py-2 text-xs">
                        {triggerRefDateFor(r) ? (
                          <span className="text-foreground">
                            {formatDate(triggerRefDateFor(r)!)}
                          </span>
                        ) : (
                          <span className="text-muted-foreground">—</span>
                        )}
                      </td>
                    )}
                    <td className="py-2">
                      <StatusPill status={r.status} sendStats={r.sendStats} />
                    </td>
                    <td className="py-2 text-muted-foreground text-xs">
                      Step {r.currentStepIndex + 1}
                      {r.status === "waiting" && r.nextRunAt && (
                        <span
                          className="ml-1 text-amber-700"
                          title={`Scheduled to send on ${formatDate(r.nextRunAt)} (Brisbane)`}
                        >
                          (sends {timeUntil(r.nextRunAt)} — {formatDate(r.nextRunAt)})
                        </span>
                      )}
                    </td>
                    <td className="py-2 text-muted-foreground text-xs">
                      {timeAgo(r.startedAt)}
                    </td>
                    <td className="py-2 pr-2 text-right">
                      {isActive && (
                        <button
                          type="button"
                          onClick={() => removeOne.mutate(r.id)}
                          disabled={removeOne.isPending}
                          className="inline-flex items-center gap-1 rounded px-2 py-1 text-xs font-medium text-red-700 hover:bg-red-50 border border-red-200"
                          title="Remove this contact from the automation"
                        >
                          <X className="h-3 w-3" />
                          Remove
                        </button>
                      )}
                    </td>
                  </tr>
                )
              })}
            </tbody>
          </table>
          {visibleItems.length > 200 && (
            <div className="pt-2 text-xs text-muted-foreground text-center">
              Showing 200 most recent. Older enrolments hidden.
            </div>
          )}
        </div>
      )}
    </Card>
  )
}

// ============================================================
// Step icon
// ============================================================
function StepIcon({ kind }: { kind: string }) {
  const Icon = STEP_ICONS[kind] ?? Route
  return (
    <span
      className={cn(
        "grid h-6 w-6 shrink-0 place-items-center rounded",
        STEP_TONES[kind] ?? "bg-rc-grey-100 text-rc-grey-700"
      )}
    >
      <Icon className="h-3.5 w-3.5" />
    </span>
  )
}
const STEP_ICONS: Record<string, typeof Mail> = {
  wait: Clock,
  send_email: Mail,
  send_sms: MessageSquare,
  create_task: CheckSquare,
  add_note: StickyNote,
  add_tag: Tag,
  remove_tag: TagIcon,
  if_else: GitBranch,
  move_deal_stage: Route,
  assign_broker: User,
  update_field: Route,
  exit: Route,
}
const STEP_TONES: Record<string, string> = {
  wait: "bg-amber-100 text-amber-700",
  send_email: "bg-blue-100 text-blue-700",
  send_sms: "bg-purple-100 text-purple-700",
  create_task: "bg-emerald-100 text-emerald-700",
  add_note: "bg-yellow-100 text-yellow-700",
  add_tag: "bg-emerald-100 text-emerald-700",
  remove_tag: "bg-rc-grey-100 text-rc-grey-700",
  if_else: "bg-slate-100 text-slate-700",
  move_deal_stage: "bg-rc-blue-100 text-rc-blue-700",
  assign_broker: "bg-rc-blue-100 text-rc-blue-700",
  update_field: "bg-rc-grey-100 text-rc-grey-700",
  exit: "bg-rc-grey-100 text-rc-grey-700",
}

function StatusBadge({ status }: { status: string }) {
  const map: Record<string, string> = {
    active: "bg-emerald-100 text-emerald-700",
    paused: "bg-amber-100 text-amber-700",
    draft: "bg-slate-100 text-slate-700",
    archived: "bg-rc-grey-100 text-rc-grey-700",
  }
  return (
    <span
      className={cn(
        "ml-1 inline-flex items-center rounded-full px-1.5 py-0.5 text-[10px] font-medium",
        map[status] ?? "bg-rc-grey-100"
      )}
    >
      {status}
    </span>
  )
}


// ============================================================
// STEP EDITOR — the actual point of this whole page
// Renders a form appropriate for the step kind.
// ============================================================
function StepEditor({
  step,
  onChange,
  triggerKind,
  workflowCommunicationType,
}: {
  step: Step
  onChange: (patch: Partial<Step>) => void
  triggerKind: string
  workflowCommunicationType: AutomationCommunicationType
}) {
  const kind = step.kind

  return (
    <div>
      <div className="mb-3 flex items-center gap-2">
        <StepIcon kind={kind} />
        <h3 className="text-base font-semibold capitalize text-foreground">
          {kind.replace(/_/g, " ")}
        </h3>
      </div>

      {(kind === "send_sms" || kind === "send_email") &&
        workflowCommunicationType === "mixed" && (
          <StepClassificationCard step={step} onChange={onChange} channel={kind === "send_sms" ? "sms" : "email"} />
        )}

      {kind === "send_email" && (
        <EmailStepEditor
          step={
            step as Step & {
              subject?: string
              body?: string
              appendSignature?: boolean
              attachmentIds?: string[]
            }
          }
          onChange={onChange}
          workflowCommunicationType={workflowCommunicationType}
        />
      )}
      {kind === "send_sms" && (
        <SmsStepEditor
          step={step as Step & { body?: string }}
          onChange={onChange}
          workflowCommunicationType={workflowCommunicationType}
        />
      )}
      {kind === "wait" && (
        <WaitStepEditor step={step as Step & { durationSeconds: number }} onChange={onChange} />
      )}
      {kind === "create_task" && (
        <TaskStepEditor step={step as Step & { title: string; description?: string; priority: string; dueInSeconds?: number }} onChange={onChange} />
      )}
      {kind === "add_note" && (
        <NoteStepEditor step={step as Step & { body: string }} onChange={onChange} />
      )}
      {(kind === "add_tag" || kind === "remove_tag") && (
        <TagStepEditor step={step as Step & { tag: string }} onChange={onChange} kind={kind} />
      )}
      {kind === "move_deal_stage" && (
        <MoveStageStepEditor step={step as Step & { pipelineStage: string }} onChange={onChange} />
      )}
      {kind === "exit" && (
        <ExitStepEditor step={step as Step & { reason?: string }} onChange={onChange} />
      )}
      {(kind === "assign_broker" || kind === "update_field" || kind === "if_else") && (
        <div className="text-muted-foreground rounded-md border border-dashed border-border p-4 text-sm">
          Editor for <code>{kind}</code> is coming soon. For now edit the JSON via
          the "Compose with AI" flow or ask support.
        </div>
      )}

      <TriggerContextHint triggerKind={triggerKind} />
    </div>
  )
}

function TriggerContextHint({ triggerKind }: { triggerKind: string }) {
  const notes: Record<string, string> = {
    balloon_due:
      "This runs against a Deal. You can use {{deal.termEndIn}}, {{deal.termEndDateFormatted}} and {{contact.firstName}} in the body.",
    birthday: "This runs against a Contact. Use {{contact.firstName}} in the body.",
    settlement_anniversary:
      "This runs against a Deal that settled. Use {{deal.settledDate}} and {{contact.firstName}}.",
    days_since_last_contact:
      "This runs against a Contact with no recent comms.",
    days_since_deal_lost: "This runs against a lost Deal.",
    contact_created: "This runs against a newly-created Contact.",
    deal_created: "This runs against a newly-created Deal.",
    deal_stage_changed: "This runs against a Deal that just changed stage.",
    task_completed: "This runs against a Task that was just completed.",
  }
  const note = notes[triggerKind]
  if (!note) return null
  return (
    <div className="border-blue-200 bg-blue-50 mt-3 rounded-md border p-2 text-xs text-blue-900">
      <strong>Context:</strong> {note}
    </div>
  )
}

// ------------------------------------------------------------
// Email step
// ------------------------------------------------------------
type EmailStep = Step & {
  subject?: string
  body?: string
  appendSignature?: boolean
  attachmentIds?: string[]
  sendWindow?: { startHour: number; endHour: number }
}

function EmailStepEditor({
  step,
  onChange,
  workflowCommunicationType,
}: {
  step: EmailStep
  onChange: (patch: Partial<Step>) => void
  workflowCommunicationType: AutomationCommunicationType
}) {
  const [preview, setPreview] = useState(false)
  const appendSig = step.appendSignature !== false
  const attachmentIds = step.attachmentIds ?? []

  // Resolve effective classification for direct/marketing display + footer toggle.
  const stepCls = (step as { classification?: StepClassification }).classification
  const effective: StepClassification =
    workflowCommunicationType === "direct"
      ? "direct"
      : workflowCommunicationType === "marketing"
        ? "marketing"
        : (stepCls ?? "marketing")
  const suppressFooter = !!(step as { suppressUnsubscribeFooter?: boolean })
    .suppressUnsubscribeFooter

  return (
    <div className="space-y-3">
      <div>
        <Label>Subject</Label>
        <Input
          value={step.subject ?? ""}
          onChange={(e) => onChange({ subject: e.target.value } as Partial<Step>)}
          placeholder="Your balloon payment is due in 90 days"
        />
      </div>

      <div>
        <div className="mb-1 flex items-center justify-between">
          <Label>Email body</Label>
          <button
            type="button"
            onClick={() => setPreview((v) => !v)}
            className="text-rc-blue-600 hover:text-rc-blue-700 text-xs font-medium"
          >
            {preview ? "← Back to editor" : "Preview →"}
          </button>
        </div>
        {preview ? (
          <EmailPreview
            subject={step.subject ?? ""}
            body={step.body ?? ""}
            appendSignature={appendSig}
          />
        ) : (
          <>
            <RichTextEditor
              value={step.body ?? ""}
              onChange={(html) => onChange({ body: html } as Partial<Step>)}
              placeholder="Hi {{contact.firstName}}, ..."
              minHeight={220}
              onImageUpload={async (file) => {
                const r = await automationsApi.uploadInlineImage(file)
                return r.url
              }}
            />
            <MergeFieldPicker
              onInsert={(k) =>
                onChange({
                  body: (step.body ?? "") + ` {{${k}}}`,
                } as Partial<Step>)
              }
            />
          </>
        )}
      </div>

      <SignatureAndAttachments
        appendSignature={appendSig}
        attachmentIds={attachmentIds}
        onAppendSignatureChange={(v) =>
          onChange({ appendSignature: v } as Partial<Step>)
        }
        onAttachmentIdsChange={(ids) =>
          onChange({ attachmentIds: ids } as Partial<Step>)
        }
      />

      {/* Compliance footer control (Phase 3) */}
      <div className="border-border/60 mt-3 rounded-md border p-3">
        <div className="mb-1 text-sm font-medium text-foreground">
          Unsubscribe footer
        </div>
        {effective === "marketing" ? (
          <p className="text-muted-foreground text-xs">
            ✅ Automatically included. Marketing emails always include a
            functional unsubscribe link and Manage Preferences link. This cannot
            be disabled.
          </p>
        ) : (
          <label className="flex cursor-pointer items-center gap-2 text-sm">
            <input
              type="checkbox"
              className="h-4 w-4 rounded border-input"
              checked={!suppressFooter}
              onChange={(e) =>
                onChange({
                  suppressUnsubscribeFooter: !e.target.checked,
                } as Partial<Step>)
              }
            />
            <span>Include unsubscribe footer</span>
            <span className="text-muted-foreground text-xs">
              (Direct/Operational emails may omit it, but only for genuine
              operational content)
            </span>
          </label>
        )}
      </div>

      <SendWindowEditor
        window={step.sendWindow}
        onChange={(w) => onChange({ sendWindow: w } as Partial<Step>)}
      />

      <TestSendEmailPanel
        subject={step.subject ?? ""}
        body={step.body ?? ""}
        appendSignature={appendSig}
        attachmentIds={attachmentIds}
        classification={effective}
        suppressUnsubscribeFooter={suppressFooter}
      />
    </div>
  )
}

// Simple email preview — merges sample values in
function EmailPreview({
  subject,
  body,
  appendSignature,
}: {
  subject: string
  body: string
  appendSignature?: boolean
}) {
  const SAMPLE: Record<string, string> = {
    "contact.firstName": "Andrew",
    "contact.lastName": "Ruyl",
    "contact.email": "andy@example.com",
    "contact.mobile": "+61 400 000 000",
    "contact.businessName": "Ruyl Holdings",
    "deal.dealName": "Nissan Navara finance",
    "deal.loanAmount": "$45,000",
    "deal.settledDate": "2025-06-16",
    "deal.termEndDate": "2028-06-16",
    "deal.termEndDateFormatted": "16 Jun 2028",
    "deal.termEndIn": "3 months",
    "deal.balloon": "$5,000",
    // Legacy aliases (kept so old templates still preview sensibly)
    "deal.balloonDueDate": "2028-06-16",
    "deal.balloonDueIn": "3 months",
    "deal.balloonDueDateFormatted": "16 Jun 2028",
    "deal.settledAgo": "12 months ago",
    "deal.settledDateFormatted": "16 Jun 2025",
    "deal.lostDateFormatted": "18 Apr 2026",
    "deal.lostAgo": "3 months ago",
    "broker.firstName": "Andy",
    "broker.email": "andy@rivercityfinance.com.au",
    "broker.mobile": "+61 468 075 029",
    today: new Date().toLocaleDateString("en-AU", {
      timeZone: "Australia/Brisbane",
    }),
  }
  const merged = (s: string) =>
    s.replace(/\{\{\s*([\w.]+)\s*\}\}/g, (_, k) => SAMPLE[k] ?? `{{${k}}}`)

  return (
    <div className="border-border rounded-md border bg-white">
      <div className="border-border border-b p-3">
        <div className="text-muted-foreground text-[10px] uppercase tracking-wide">
          Subject
        </div>
        <div className="text-sm font-semibold text-foreground">{merged(subject) || "(no subject)"}</div>
      </div>
      <div
        className="prose prose-sm max-w-none p-4 text-sm text-foreground"
        // eslint-disable-next-line react/no-danger
        dangerouslySetInnerHTML={{
          __html:
            merged(body) ||
            '<span class="italic text-muted-foreground">(no body)</span>',
        }}
      />
      {appendSignature && <SignaturePreviewInline />}
      <div className="border-border border-t p-2 text-[11px] text-muted-foreground">
        Preview with sample data — real sends use the enrolled contact + broker.
      </div>
    </div>
  )
}

// ------------------------------------------------------------
// SMS step
// ------------------------------------------------------------
// ------------------------------------------------------------
// Send-window editor — shared between SMS and Email steps.
// Times are ALWAYS Australia/Brisbane wall-clock hours.
// ------------------------------------------------------------
function SendWindowEditor({
  window,
  onChange,
}: {
  window: { startHour: number; endHour: number } | undefined
  onChange: (w: { startHour: number; endHour: number } | undefined) => void
}) {
  const enabled = !!window
  const startHour = window?.startHour ?? 8
  const endHour = window?.endHour ?? 18
  const fmt = (h: number) => {
    if (h === 0) return "12am"
    if (h === 12) return "12pm"
    if (h === 24) return "12am (next day)"
    return h > 12 ? `${h - 12}pm` : `${h}am`
  }
  return (
    <div className="border-border/60 mt-3 rounded-md border p-3">
      <label className="flex items-center gap-2 text-sm font-medium">
        <input
          type="checkbox"
          checked={enabled}
          onChange={(e) =>
            onChange(e.target.checked ? { startHour, endHour } : undefined)
          }
        />
        <Clock className="h-4 w-4" />
        Only send during a set time window (Brisbane time)
      </label>
      {enabled && (
        <div className="mt-3 grid grid-cols-2 gap-3">
          <div>
            <Label className="text-xs">Earliest send</Label>
            <select
              className="border-input bg-background mt-1 h-9 w-full rounded-md border px-2 text-sm"
              value={startHour}
              onChange={(e) => onChange({ startHour: Number(e.target.value), endHour })}
            >
              {Array.from({ length: 24 }, (_, h) => (
                <option key={h} value={h}>{fmt(h)}</option>
              ))}
            </select>
          </div>
          <div>
            <Label className="text-xs">Latest send</Label>
            <select
              className="border-input bg-background mt-1 h-9 w-full rounded-md border px-2 text-sm"
              value={endHour}
              onChange={(e) => onChange({ startHour, endHour: Number(e.target.value) })}
            >
              {Array.from({ length: 24 }, (_, i) => i + 1).map((h) => (
                <option key={h} value={h}>{fmt(h)}</option>
              ))}
            </select>
          </div>
          <p className="text-muted-foreground col-span-2 text-[11px]">
            If the automation reaches this step outside the window, it waits and
            sends at the next window opening. All times are Australia/Brisbane.
          </p>
        </div>
      )}
      {!enabled && (
        <p className="text-muted-foreground mt-1 text-[11px]">
          Sends immediately whenever the automation reaches this step.
        </p>
      )}
    </div>
  )
}

function SmsStepEditor({
  step,
  onChange,
  workflowCommunicationType,
}: {
  step: Step & {
    body?: string
    sendWindow?: { startHour: number; endHour: number }
    classification?: StepClassification
    suppressOptOutWording?: boolean
  }
  onChange: (patch: Partial<Step>) => void
  workflowCommunicationType: AutomationCommunicationType
}) {
  const body = step.body ?? ""
  const chars = body.length
  const segments = Math.ceil(chars / 160) || 1

  const effective: StepClassification =
    workflowCommunicationType === "direct"
      ? "direct"
      : workflowCommunicationType === "marketing"
        ? "marketing"
        : (step.classification ?? "marketing")
  const suppressWording = !!step.suppressOptOutWording

  return (
    <div className="space-y-3">
      <div>
        <Label>SMS body</Label>
        <Textarea
          value={body}
          onChange={(e) => onChange({ body: e.target.value } as Partial<Step>)}
          rows={5}
          placeholder="Hi {{contact.firstName}}, it's {{broker.firstName}} from Rivercity Finance…"
          className="font-mono text-sm"
        />
        <div className="text-muted-foreground mt-1 flex justify-between text-[11px]">
          <span>
            {chars} chars · {segments} segment{segments === 1 ? "" : "s"}
          </span>
          <span>Aim for &lt;160 chars per segment</span>
        </div>
        <MergeFieldPicker
          onInsert={(k) => onChange({ body: (body ?? "") + `{{${k}}}` } as Partial<Step>)}
        />
        <p className="text-muted-foreground mt-1 text-[11px]">
          Don't include "Reply STOP to opt out" here — for marketing sends it's
          appended automatically.
        </p>
      </div>

      {/* Compliance opt-out control (Phase 3) */}
      <div className="border-border/60 rounded-md border p-3">
        <div className="mb-1 text-sm font-medium text-foreground">
          STOP opt-out wording
        </div>
        {effective === "marketing" ? (
          <p className="text-muted-foreground text-xs">
            ✅ Automatically included. Marketing SMS always includes
            "Reply STOP to opt out". This cannot be disabled.
          </p>
        ) : (
          <label className="flex cursor-pointer items-center gap-2 text-sm">
            <input
              type="checkbox"
              className="h-4 w-4 rounded border-input"
              checked={!suppressWording}
              onChange={(e) =>
                onChange({
                  suppressOptOutWording: !e.target.checked,
                } as Partial<Step>)
              }
            />
            <span>Append "Reply STOP to opt out"</span>
            <span className="text-muted-foreground text-xs">
              (Direct/Operational SMS may omit STOP wording)
            </span>
          </label>
        )}
      </div>

      <SendWindowEditor
        window={step.sendWindow}
        onChange={(w) => onChange({ sendWindow: w } as Partial<Step>)}
      />

      <TestSendSmsPanel
        body={body}
        classification={effective}
        suppressOptOutWording={suppressWording}
      />
    </div>
  )
}

// ------------------------------------------------------------
// Wait step
// ------------------------------------------------------------
function WaitStepEditor({
  step,
  onChange,
}: {
  step: Step & { durationSeconds: number }
  onChange: (patch: Partial<Step>) => void
}) {
  const secs = step.durationSeconds ?? 0
  let amount = secs
  let unit: "seconds" | "minutes" | "hours" | "days" = "seconds"
  if (secs % 86400 === 0 && secs >= 86400) {
    unit = "days"
    amount = secs / 86400
  } else if (secs % 3600 === 0 && secs >= 3600) {
    unit = "hours"
    amount = secs / 3600
  } else if (secs % 60 === 0 && secs >= 60) {
    unit = "minutes"
    amount = secs / 60
  }
  const [n, setN] = useState<number>(amount)
  const [u, setU] = useState(unit)

  const apply = (nextN: number, nextU: typeof unit) => {
    const mult =
      nextU === "days" ? 86400 : nextU === "hours" ? 3600 : nextU === "minutes" ? 60 : 1
    onChange({ durationSeconds: Math.max(1, Math.round(nextN * mult)) } as Partial<Step>)
  }

  return (
    <div className="space-y-3">
      <div>
        <Label>Wait duration</Label>
        <div className="flex gap-2">
          <Input
            type="number"
            min={1}
            value={n}
            onChange={(e) => {
              const v = Number(e.target.value) || 0
              setN(v)
              apply(v, u)
            }}
            className="w-32"
          />
          <select
            className="border-input bg-background h-10 rounded-md border px-3 text-sm"
            value={u}
            onChange={(e) => {
              const nu = e.target.value as typeof unit
              setU(nu)
              apply(n, nu)
            }}
          >
            <option value="seconds">seconds</option>
            <option value="minutes">minutes</option>
            <option value="hours">hours</option>
            <option value="days">days</option>
          </select>
        </div>
        <p className="text-muted-foreground mt-1 text-xs">
          The next step runs {n} {u} after this one.
        </p>
      </div>
    </div>
  )
}

// ------------------------------------------------------------
// Create task step
// ------------------------------------------------------------
function TaskStepEditor({
  step,
  onChange,
}: {
  step: Step & { title: string; description?: string; priority: string; dueInSeconds?: number }
  onChange: (patch: Partial<Step>) => void
}) {
  return (
    <div className="space-y-3">
      <div>
        <Label>Task title</Label>
        <Input
          value={step.title ?? ""}
          onChange={(e) => onChange({ title: e.target.value } as Partial<Step>)}
          placeholder="e.g. Call {{contact.firstName}} about balloon options"
        />
      </div>
      <div>
        <Label>Description (optional)</Label>
        <Textarea
          value={step.description ?? ""}
          onChange={(e) => onChange({ description: e.target.value } as Partial<Step>)}
          rows={3}
        />
      </div>
      <div className="grid grid-cols-2 gap-3">
        <div>
          <Label>Priority</Label>
          <select
            className="border-input bg-background h-10 w-full rounded-md border px-3 text-sm"
            value={step.priority ?? "medium"}
            onChange={(e) => onChange({ priority: e.target.value } as Partial<Step>)}
          >
            <option value="low">Low</option>
            <option value="medium">Medium</option>
            <option value="high">High</option>
          </select>
        </div>
        <div>
          <Label>Due in (days from now)</Label>
          <Input
            type="number"
            min={0}
            value={
              step.dueInSeconds != null ? Math.round(step.dueInSeconds / 86400) : ""
            }
            onChange={(e) => {
              const days = Number(e.target.value)
              onChange({
                dueInSeconds: isNaN(days) ? undefined : days * 86400,
              } as Partial<Step>)
            }}
            placeholder="—"
          />
        </div>
      </div>
    </div>
  )
}

// ------------------------------------------------------------
// Note step
// ------------------------------------------------------------
function NoteStepEditor({
  step,
  onChange,
}: {
  step: Step & { body: string }
  onChange: (patch: Partial<Step>) => void
}) {
  return (
    <div>
      <Label>Note body</Label>
      <Textarea
        value={step.body ?? ""}
        onChange={(e) => onChange({ body: e.target.value } as Partial<Step>)}
        rows={5}
        placeholder="Note that gets added to the contact's timeline"
      />
    </div>
  )
}

// ------------------------------------------------------------
// Tag step
// ------------------------------------------------------------
function TagStepEditor({
  step,
  onChange,
  kind,
}: {
  step: Step & { tag: string }
  onChange: (patch: Partial<Step>) => void
  kind: string
}) {
  return (
    <div>
      <Label>{kind === "add_tag" ? "Tag to add" : "Tag to remove"}</Label>
      <Input
        value={step.tag ?? ""}
        onChange={(e) => onChange({ tag: e.target.value } as Partial<Step>)}
        placeholder="e.g. hot-lead"
      />
    </div>
  )
}

// ------------------------------------------------------------
// Move deal stage
// ------------------------------------------------------------
const PIPELINE_STAGES = [
  "new_lead",
  "day_one",
  "day_two",
  "day_three",
  "general_followup",
  "awaiting_docs",
  "docs_received",
  "approved",
  "settled",
  "closed",
]
function MoveStageStepEditor({
  step,
  onChange,
}: {
  step: Step & { pipelineStage: string }
  onChange: (patch: Partial<Step>) => void
}) {
  return (
    <div>
      <Label>Move deal to stage</Label>
      <select
        className="border-input bg-background h-10 w-full rounded-md border px-3 text-sm"
        value={step.pipelineStage}
        onChange={(e) => onChange({ pipelineStage: e.target.value } as Partial<Step>)}
      >
        {PIPELINE_STAGES.map((s) => (
          <option key={s} value={s}>
            {s.replace(/_/g, " ")}
          </option>
        ))}
      </select>
    </div>
  )
}

// ------------------------------------------------------------
// Exit
// ------------------------------------------------------------
function ExitStepEditor({
  step,
  onChange,
}: {
  step: Step & { reason?: string }
  onChange: (patch: Partial<Step>) => void
}) {
  return (
    <div>
      <Label>Exit reason (optional)</Label>
      <Input
        value={step.reason ?? ""}
        onChange={(e) => onChange({ reason: e.target.value } as Partial<Step>)}
        placeholder="e.g. Client responded"
      />
      <p className="text-muted-foreground mt-1 text-xs">
        No further steps run after this. The enrolment is marked exited.
      </p>
    </div>
  )
}

// ============================================================
// Signature + attachment controls (used by email step)
// ============================================================
function SignatureAndAttachments({
  appendSignature,
  attachmentIds,
  onAppendSignatureChange,
  onAttachmentIdsChange,
}: {
  appendSignature: boolean
  attachmentIds: string[]
  onAppendSignatureChange: (v: boolean) => void
  onAttachmentIdsChange: (ids: string[]) => void
}) {
  const qc = useQueryClient()

  // Load metadata for the current attachment ids so we can show file names
  const attachmentsQ = useQuery({
    queryKey: ["automation-attachments", attachmentIds],
    queryFn: () =>
      attachmentIds.length > 0
        ? automationsApi.listAttachments(attachmentIds)
        : Promise.resolve({ items: [] }),
    enabled: attachmentIds.length > 0,
  })

  // Load broker's saved signature so we can show a hint
  const signatureQ = useQuery({
    queryKey: ["my-signature"],
    queryFn: () => api.get<{ signature: { html: string } | null }>("/templates/-/signature"),
  })
  const hasSignature = !!signatureQ.data?.signature?.html

  const uploadMutation = useMutation({
    mutationFn: (file: File) => automationsApi.uploadAttachment(file),
    onSuccess: (att) => {
      onAttachmentIdsChange([...attachmentIds, att.id])
      qc.invalidateQueries({ queryKey: ["automation-attachments"] })
    },
  })

  const deleteMutation = useMutation({
    mutationFn: (id: string) => automationsApi.deleteAttachment(id),
    onSuccess: (_r, id) => {
      onAttachmentIdsChange(attachmentIds.filter((x) => x !== id))
      qc.invalidateQueries({ queryKey: ["automation-attachments"] })
    },
  })

  const fileInputRef = useRef<HTMLInputElement>(null)

  return (
    <div className="border-border bg-rc-grey-50/40 rounded-md border p-3 space-y-3">
      {/* Signature toggle */}
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-1.5 text-xs font-semibold text-foreground">
            <PenLine className="h-3.5 w-3.5 text-rc-blue-600" />
            Include my signature
          </div>
          <p className="text-muted-foreground mt-0.5 text-[11px]">
            {hasSignature
              ? "Your saved signature is appended after the email body."
              : "You don't have a saved signature yet. Set one in My Settings."}
          </p>
        </div>
        <label className="relative inline-flex h-5 w-9 shrink-0 cursor-pointer items-center">
          <input
            type="checkbox"
            checked={appendSignature}
            onChange={(e) => onAppendSignatureChange(e.target.checked)}
            className="peer sr-only"
          />
          <span className="peer-checked:bg-rc-blue-600 h-5 w-9 rounded-full bg-rc-grey-300 transition-colors" />
          <span className="absolute left-0.5 top-0.5 h-4 w-4 rounded-full bg-white shadow transition-transform peer-checked:translate-x-4" />
        </label>
      </div>

      {/* Attachments */}
      <div>
        <div className="flex items-center justify-between gap-2">
          <div className="flex items-center gap-1.5 text-xs font-semibold text-foreground">
            <Paperclip className="h-3.5 w-3.5 text-rc-blue-600" />
            Attachments {attachmentIds.length > 0 && <span className="text-muted-foreground font-normal">({attachmentIds.length})</span>}
          </div>
          <Button
            type="button"
            size="sm"
            variant="outline"
            onClick={() => fileInputRef.current?.click()}
            disabled={uploadMutation.isPending}
            className="h-7 text-xs"
          >
            {uploadMutation.isPending ? (
              <Loader2 className="h-3.5 w-3.5 animate-spin" />
            ) : (
              <Upload className="h-3.5 w-3.5" />
            )}
            Add file
          </Button>
          <input
            ref={fileInputRef}
            type="file"
            className="hidden"
            accept=".pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.png,.jpg,.jpeg,.gif,.webp,.txt,.csv"
            onChange={(e) => {
              const f = e.target.files?.[0]
              if (f) uploadMutation.mutate(f)
              e.target.value = ""
            }}
          />
        </div>

        {uploadMutation.isError && (
          <div className="border-destructive/40 bg-destructive/5 text-destructive mt-2 rounded border p-2 text-xs">
            {uploadMutation.error instanceof Error
              ? uploadMutation.error.message
              : "Upload failed"}
          </div>
        )}

        {attachmentIds.length === 0 ? (
          <p className="text-muted-foreground mt-1 text-[11px] italic">
            No files attached. Add PDFs, docs, or images (max 10MB each).
          </p>
        ) : (
          <ul className="mt-2 space-y-1">
            {(attachmentsQ.data?.items ?? []).map((a) => (
              <li
                key={a.id}
                className="border-border flex items-center gap-2 rounded border bg-white p-2 text-xs"
              >
                <FileText className="h-3.5 w-3.5 shrink-0 text-rc-blue-600" />
                <div className="min-w-0 flex-1">
                  <div className="truncate text-foreground">{a.filename}</div>
                  <div className="text-muted-foreground text-[10px]">
                    {formatBytes(a.sizeBytes)}
                  </div>
                </div>
                <button
                  type="button"
                  onClick={() => deleteMutation.mutate(a.id)}
                  disabled={deleteMutation.isPending}
                  className="text-muted-foreground hover:text-destructive shrink-0 p-1"
                  title="Remove attachment"
                >
                  <X className="h-3.5 w-3.5" />
                </button>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  )
}

function SignaturePreviewInline() {
  const signatureQ = useQuery({
    queryKey: ["my-signature"],
    queryFn: () => api.get<{ signature: { html: string } | null }>("/templates/-/signature"),
  })
  const html = signatureQ.data?.signature?.html
  if (!html) return null

  return (
    <div
      className="prose prose-sm max-w-none px-4 pb-4 text-sm text-foreground"
      // eslint-disable-next-line react/no-danger
      dangerouslySetInnerHTML={{ __html: html }}
    />
  )
}

function formatBytes(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`
  return `${(bytes / 1024 / 1024).toFixed(1)} MB`
}

// ============================================================
// Test send panels
// ============================================================
function TestSendEmailPanel({
  subject,
  body,
  appendSignature,
  attachmentIds,
  classification,
  suppressUnsubscribeFooter,
}: {
  subject: string
  body: string
  appendSignature?: boolean
  attachmentIds?: string[]
  classification?: "direct" | "marketing"
  suppressUnsubscribeFooter?: boolean
}) {
  const { user } = useAuth()
  const [to, setTo] = useState<string>(user?.email ?? "")
  const [status, setStatus] = useState<
    { kind: "idle" } | { kind: "ok"; message: string } | { kind: "error"; message: string }
  >({ kind: "idle" })

  const send = useMutation({
    mutationFn: () =>
      automationsApi.testSendEmail({
        toEmail: to,
        subject,
        body,
        appendSignature: appendSignature !== false,
        attachmentIds: attachmentIds ?? [],
        classification,
        suppressUnsubscribeFooter,
      }),
    onSuccess: (r) => {
      if (r.ok) setStatus({ kind: "ok", message: `Sent to ${to} — check your inbox` })
      else setStatus({ kind: "error", message: r.error })
    },
    onError: (e) =>
      setStatus({ kind: "error", message: e instanceof Error ? e.message : "Send failed" }),
  })

  return (
    <div className="border-border bg-rc-grey-50/60 mt-3 rounded-md border p-3">
      <div className="mb-2 flex items-center gap-1.5 text-xs font-semibold text-foreground">
        <Send className="h-3.5 w-3.5 text-rc-blue-600" />
        Send a test email
      </div>
      <div className="flex flex-wrap gap-2">
        <Input
          type="email"
          value={to}
          onChange={(e) => setTo(e.target.value)}
          placeholder="you@example.com"
          className="h-9 min-w-[220px] flex-1"
        />
        <Button
          type="button"
          onClick={() => {
            setStatus({ kind: "idle" })
            send.mutate()
          }}
          disabled={!to || send.isPending || !body.trim()}
          size="sm"
        >
          {send.isPending ? (
            <Loader2 className="h-3.5 w-3.5 animate-spin" />
          ) : (
            <Send className="h-3.5 w-3.5" />
          )}
          Send test
        </Button>
      </div>
      <p className="text-muted-foreground mt-1 text-[11px]">
        Sends via your connected Outlook mailbox. Merge fields render with
        sample values (contact.firstName → “Andrew” etc).
      </p>
      {status.kind === "ok" && (
        <div className="border-emerald-200 bg-emerald-50 text-emerald-800 mt-2 flex items-start gap-1.5 rounded border p-2 text-xs">
          <Check className="mt-0.5 h-3.5 w-3.5 shrink-0" />
          <span>{status.message}</span>
        </div>
      )}
      {status.kind === "error" && (
        <div className="border-destructive/40 bg-destructive/5 text-destructive mt-2 flex items-start gap-1.5 rounded border p-2 text-xs">
          <AlertCircle className="mt-0.5 h-3.5 w-3.5 shrink-0" />
          <span>{status.message}</span>
        </div>
      )}
    </div>
  )
}

function TestSendSmsPanel({
  body,
  classification,
  suppressOptOutWording,
}: {
  body: string
  classification?: "direct" | "marketing"
  suppressOptOutWording?: boolean
}) {
  const [to, setTo] = useState<string>("")
  const [status, setStatus] = useState<
    { kind: "idle" } | { kind: "ok"; message: string } | { kind: "error"; message: string }
  >({ kind: "idle" })

  const send = useMutation({
    mutationFn: () =>
      automationsApi.testSendSms({
        toPhone: to,
        body,
        classification,
        suppressOptOutWording,
      }),
    onSuccess: (r) => {
      if (r.ok) setStatus({ kind: "ok", message: `Sent to ${to}— check your phone` })
      else setStatus({ kind: "error", message: r.error })
    },
    onError: (e) =>
      setStatus({ kind: "error", message: e instanceof Error ? e.message : "Send failed" }),
  })

  return (
    <div className="border-border bg-rc-grey-50/60 mt-3 rounded-md border p-3">
      <div className="mb-2 flex items-center gap-1.5 text-xs font-semibold text-foreground">
        <Send className="h-3.5 w-3.5 text-rc-blue-600" />
        Send a test SMS
      </div>
      <div className="flex flex-wrap gap-2">
        <Input
          type="tel"
          value={to}
          onChange={(e) => setTo(e.target.value)}
          placeholder="+61 4XX XXX XXX"
          className="h-9 min-w-[180px] flex-1"
        />
        <Button
          type="button"
          onClick={() => {
            setStatus({ kind: "idle" })
            send.mutate()
          }}
          disabled={!to || send.isPending || !body.trim()}
          size="sm"
        >
          {send.isPending ? (
            <Loader2 className="h-3.5 w-3.5 animate-spin" />
          ) : (
            <Send className="h-3.5 w-3.5" />
          )}
          Send test
        </Button>
      </div>
      <p className="text-muted-foreground mt-1 text-[11px]">
        Sends via your Aircall number. Merge fields render with sample values.
      </p>
      {status.kind === "ok" && (
        <div className="border-emerald-200 bg-emerald-50 text-emerald-800 mt-2 flex items-start gap-1.5 rounded border p-2 text-xs">
          <Check className="mt-0.5 h-3.5 w-3.5 shrink-0" />
          <span>{status.message}</span>
        </div>
      )}
      {status.kind === "error" && (
        <div className="border-destructive/40 bg-destructive/5 text-destructive mt-2 flex items-start gap-1.5 rounded border p-2 text-xs">
          <AlertCircle className="mt-0.5 h-3.5 w-3.5 shrink-0" />
          <span>{status.message}</span>
        </div>
      )}
    </div>
  )
}

// ============================================================
// Merge field picker
// ============================================================
function MergeFieldPicker({ onInsert }: { onInsert: (key: string) => void }) {
  const groups = useMemo(() => {
    const m = new Map<string, typeof MERGE_FIELDS>()
    for (const f of MERGE_FIELDS) {
      const arr = m.get(f.group) ?? []
      arr.push(f)
      m.set(f.group, arr)
    }
    return Array.from(m.entries())
  }, [])
  const [copied, setCopied] = useState<string | null>(null)

  return (
    <div className="border-border mt-2 rounded-md border bg-rc-grey-50 p-2">
      <div className="text-muted-foreground mb-1 flex items-center gap-1 text-[11px] font-semibold uppercase tracking-wide">
        <Sparkles className="h-3 w-3" />
        Merge fields — click to insert or copy
      </div>
      <div className="space-y-1">
        {groups.map(([grp, fields]) => (
          <div key={grp}>
            <div className="text-muted-foreground text-[10px]">{grp}</div>
            <div className="mt-0.5 flex flex-wrap gap-1">
              {fields.map((f) => (
                <div key={f.key} className="inline-flex items-center gap-0.5">
                  <button
                    type="button"
                    onClick={() => onInsert(f.key)}
                    className="border-border hover:border-rc-blue-400 hover:bg-white rounded border bg-white/60 px-1.5 py-0.5 font-mono text-[10px] text-foreground"
                    title={`Insert {{${f.key}}}`}
                  >
                    {`{{${f.key}}}`}
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      void navigator.clipboard.writeText(`{{${f.key}}}`)
                      setCopied(f.key)
                      setTimeout(() => setCopied(null), 1200)
                    }}
                    className="text-muted-foreground hover:text-foreground p-0.5"
                    title="Copy"
                  >
                    {copied === f.key ? (
                      <span className="text-emerald-600 text-[10px]">✓</span>
                    ) : (
                      <Copy className="h-2.5 w-2.5" />
                    )}
                  </button>
                </div>
              ))}
            </div>
          </div>
        ))}
      </div>
    </div>
  )
}

/**
 * (insertAtCursor helper was removed when we switched the email body to a
 *  TipTap rich text editor.)
 */


